/**
 * OneTrust adapter: the fan's active consent groups, expressed as the SDK's three categories.
 *
 * The SDK makes no consent decisions of its own — it calls a predicate before it starts and drops
 * everything while that predicate is falsy. This module is that predicate, plus the change feed
 * that tells the caller when to re-ask.
 *
 * Only `analytics` is ever granted. `personalization` and `advertising` stay false because no
 * category has been designated for them, and the SDK drops any event declaring a false category —
 * so sponsor and ad events cannot fire by accident.
 */

/**
 * Events that mean "the consent answer may have changed".
 *
 * `OneTrustGroupsUpdated` is OneTrust's own. `SPOptanonWrapperCalled` is dispatched by the theme's
 * `OptanonWrapper()` bridge in includes-consent/consent-compliance.php, and is the signal that
 * OneTrust has booted with previously saved preferences — a returning visitor may produce no
 * `OneTrustGroupsUpdated` at all.
 */
export const CONSENT_EVENTS = [ 'OneTrustGroupsUpdated', 'SPOptanonWrapperCalled' ];

/**
 * @typedef {Object} ConsentCategories
 * @property {boolean} analytics       Pageviews, scroll, video, custom events.
 * @property {boolean} personalization Segmentation signals. Never granted here.
 * @property {boolean} advertising     Sponsor and ad events. Never granted here.
 */

/**
 * @callback ConsentReader
 * @return {ConsentCategories} The current decision.
 */

/**
 * @callback ConsentSubscriber
 * @param {Function} listener Called with each new decision.
 * @return {Function} Unsubscribe.
 */

/**
 * @typedef {Object} ConsentSource
 * @property {ConsentReader}     read      Synchronous, side-effect free: the SDK's predicate.
 * @property {ConsentSubscriber} subscribe Change feed. Genuine changes only.
 */

/**
 * Read the OneTrust global.
 *
 * @return {string[]} Active group ids, or an empty array before OneTrust has answered.
 */
function readOneTrustGroups() {
	const raw = typeof window === 'undefined' ? '' : window.OnetrustActiveGroups;

	return String( raw || '' )
		.split( ',' )
		.map( group => group.trim() )
		.filter( Boolean );
}

/**
 * A stable string for a decision, so two decisions can be compared for equality.
 *
 * @param {ConsentCategories} categories Decision to serialise.
 * @return {string} Comparison key.
 */
function toKey( categories ) {
	return `${ categories.analytics }|${ categories.personalization }|${ categories.advertising }`;
}

/**
 * Build the consent source.
 *
 * @param {Object}         options               Collaborators.
 * @param {string}         options.group         OneTrust group id that grants analytics.
 * @param {() => string[]} [options.readGroups]  Reader for the active groups.
 * @param {EventTarget}    [options.eventTarget] Where the consent events are dispatched.
 * @return {ConsentSource} The source.
 */
export function createConsentSource( {
	group,
	readGroups = readOneTrustGroups,
	eventTarget = typeof document === 'undefined' ? null : document,
} ) {
	/**
	 * @return {ConsentCategories} The current decision.
	 */
	function read() {
		return {
			analytics: readGroups().includes( group ),
			personalization: false,
			advertising: false,
		};
	}

	/**
	 * Notify only on a real change.
	 *
	 * OneTrust fires its events on every interaction with the banner, including ones that leave
	 * our group untouched. The caller responds by restarting the tracker, and a restart re-emits
	 * the automatic pageview — so an unfiltered feed would inflate pageviews on every visit where
	 * the fan opens the preference centre.
	 *
	 * @param {(categories: ConsentCategories) => void} listener Called with the new decision.
	 * @return {() => void} Unsubscribe.
	 */
	function subscribe( listener ) {
		if ( ! eventTarget ) {
			return () => {};
		}

		// Seeded with the value at subscription time, so the first notification is a change from
		// what the caller has already acted on rather than from an assumed default.
		let lastKey = toKey( read() );

		const handler = () => {
			const categories = read();
			const key = toKey( categories );

			if ( key === lastKey ) {
				return;
			}

			lastKey = key;
			listener( categories );
		};

		CONSENT_EVENTS.forEach( name => eventTarget.addEventListener( name, handler ) );

		return () => {
			CONSENT_EVENTS.forEach( name => eventTarget.removeEventListener( name, handler ) );
		};
	}

	return { read, subscribe };
}
